import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Meter } from "@/components/common";

export default function Billing() {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { data: me } = useQ("/me");
  const { data: plans = [] } = useQ("/billing/plans");
  const [busy, setBusy] = useState("");

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const cancel = async () => {
    if (!window.confirm(t("cancel_confirm"))) return;
    try { await api.post("/billing/cancel"); qc.invalidateQueries(); toast.success(t("cancelled")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!me) return <div className="h-64 rounded-lg bg-white/[0.03] animate-pulse" />;
  const u = me.usage, l = me.limits;

  return (
    <div>
      <PageHeader eyebrow={t("nav_billing")} title={t("billing_title")} subtitle={t("billing_sub")} />
      <div className="grid lg:grid-cols-3 gap-4 mb-10">
        <Panel className="beam" data-testid="current-plan-card">
          <p className="eyebrow">{t("current_plan")}</p>
          <p className="font-display text-4xl font-black mt-3 capitalize" data-testid="current-plan-name">{me.plan}</p>
          <p className="text-sm text-slate-400 mt-1" data-testid="current-subscription-price">{me.plan === "free" ? t("free_plan_hint") : me.subscription_price != null ? `€${me.subscription_price}/${t("month")}` : t("existing_subscription_price")}</p>
          {me.plan !== "free" && <Button variant="outline" size="sm" className="mt-6 text-rose-300" onClick={cancel} data-testid="cancel-subscription-btn">{t("cancel_sub")}</Button>}
        </Panel>
        <Panel className="lg:col-span-2 grid sm:grid-cols-2 gap-6" data-testid="usage-meters">
          <Meter label={t("ai_agents")} used={u.agents} total={l.agents} testId="bill-meter-agents" />
          <Meter label={t("documents")} used={u.documents} total={l.documents} testId="bill-meter-docs" />
          <Meter label={t("ai_responses_mo")} used={u.ai_messages || 0} total={l.ai_messages} testId="bill-meter-ai" />
          <Meter label={t("stream_hours_mo")} used={Math.round((u.stream_seconds || 0) / 360) / 10} total={l.stream_hours} testId="bill-meter-hours" />
        </Panel>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.filter((p) => p.price > 0).map((p) => {
          const current = me.plan === p.id;
          return (
            <Panel key={p.id} className={`flex flex-col ${p.id === "pro" ? "border-indigo-500/40" : ""}`} data-testid={`plan-card-${p.id}`}>
              <p className="eyebrow">{p.name}</p>
              <p className="font-display text-4xl font-black mt-3">€{p.price}<span className="text-sm font-normal text-slate-400">/{t("month")}</span></p>
              <ul className="mt-6 space-y-2 text-sm text-slate-300 flex-1">
                {[`${p.agents} ${t(p.agents === 1 ? "ai_agent" : "ai_agents")}`, `${p.documents} ${t("documents")}`, `${p.ai_messages.toLocaleString()} ${t("ai_responses_mo")}`, `${p.stream_hours} ${t("stream_hours_mo")}`].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 mt-0.5" />{x}</li>
                ))}
              </ul>
              <Button disabled={current || !!busy} onClick={() => checkout(p.id)} className={`mt-6 rounded-full ${p.id === "pro" ? "bg-indigo-600 hover:bg-indigo-500" : ""}`} variant={p.id === "pro" ? "default" : "outline"} data-testid={`plan-upgrade-btn-${p.id}`}>
                {current ? t("current_plan") : busy === p.id ? t("redirecting") : t("choose_plan")}
              </Button>
            </Panel>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-6">{t("stripe_test_note")}</p>
    </div>
  );
}
